import { useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import {
  ApiError,
  assignTask,
  createTask,
  fetchErpShipments,
  fetchMembers,
  fetchRoleAssignments,
  fetchTaskTypes,
  setTaskDocuments,
} from '../api/client';
import { useSession } from '../app/WorkforceContext';
import { useAsync } from '../app/useAsync';
import { DocumentChecklistPicker } from '../components/DocumentChecklistPicker';
import { createIdempotencyKeyHolder } from '../lib/idempotency';
import {
  addItem,
  emptyItem,
  removeItem,
  isTukarFaktur,
  judulTugas,
  requiresDeliveryBlock,
  validateDeliveryBlock,
  type DeliveryBlockDraft,
} from '../lib/delivery-form';
import { kirimTugasBaru } from '../lib/badan-tugas';
import {
  judulDariSuratJalan,
  jumlahRingkas,
  kunciIdempotensiErp,
  labelSuratJalan,
  type SuratJalanErp,
} from '../lib/tarik-do';
import { checklistPayload, presetLengkap } from '../lib/task-documents';
import { kurirTersedia, type Member } from '../lib/worker-candidates';
import { ErrorCard, LoadingState } from '../states';

const PRIORITIES = [
  { value: 'urgent', label: 'Mendesak' },
  { value: 'high', label: 'Tinggi' },
  { value: 'medium', label: 'Sedang' },
  { value: 'low', label: 'Rendah' },
] as const;

/**
 * Nama opsi kosong pada pemilih surat jalan.
 *
 * SATU sumber karena dipakai DUA kali: sebagai opsinya sendiri, dan di dalam kalimat petunjuk
 * yang menyuruh manajer kembali ke sana. Sebelumnya keduanya ditulis terpisah — mengganti yang
 * satu tanpa yang lain menghasilkan petunjuk yang menyebut menu yang sudah tidak ada, dan yang
 * membacanya adalah orang yang sedang mencari jalan keluar dari isian terkunci.
 */
const OPSI_TANPA_DO = 'Pilih No. DO';

/**
 * Buat tugas.
 *
 * Dua penolakan server yang layak dijelaskan LEBIH AWAL, bukan ditampilkan sebagai error mentah
 * setelah pengguna mengetik seluruh formulir:
 *
 * - `sales_visit` (`TASK-010`) — Kunjungan sales milik CRM. Jenis itu tidak pernah muncul di
 *   pilihan; server tetap menolaknya bila dipaksa.
 * - `courier_delivery` tanpa blok `delivery` (`TASK-009`) — blok pengiriman muncul begitu jenis
 *   itu dipilih, lengkap dengan daftar barang dan penerima.
 *
 * Pada pengiriman, tugas dibuat bersama DUA hal yang selama ini terpisah: daftar barang, dan
 * daftar berkas penagihan yang dibawa kurir ("tukar faktur"). Keduanya disiapkan di satu layar
 * karena keduanya disiapkan pada saat yang sama oleh orang yang sama — manajer, sebelum kurir
 * berangkat. Menunda daftar berkas ke layar lain berarti kurir berangkat dengan berkas yang
 * belum pernah didaftarkan, dan tidak ada lagi yang bisa ditagih setelahnya.
 */
export function TaskNewPage(): ReactNode {
  const session = useSession();
  const navigate = useNavigate();
  // ADR-0054 Fase 2 — tiket servis dibuat dari kartu alat membawa `?assetId=`; tiket lalu
  // menunjuk alat lewat externalRef (hanya bila bukan tugas tarik surat jalan ERP).
  const [searchParams] = useSearchParams();
  const assetIdParam = searchParams.get('assetId');
  const keys = useMemo(() => createIdempotencyKeyHolder(), []);

  /*
   * Jenis tugas DAN daftar orang ditarik bersama, satu kali.
   *
   * Daftar orang dibutuhkan bukan oleh formulirnya melainkan oleh langkah SESUDAH simpan —
   * tetapi menariknya belakangan berarti menambah hook bersyarat, dan itu cacat yang baru saja
   * menjatuhkan halaman detail (React #310). Satu panggilan lebih awal jauh lebih murah daripada
   * satu aturan hook yang dilanggar.
   */
  const { state } = useAsync(async () => {
    const [types, members, roles, suratJalan] = await Promise.all([
      fetchTaskTypes(session.auth),
      /*
       * Kegagalan memuat daftar orang TIDAK ditelan menjadi daftar kosong.
       *
       * `.catch(() => [])` membuat panggilan yang GAGAL terbaca persis seperti "memang tidak ada
       * anggota" — dan layarnya lalu mengucapkan kalimat yang tidak benar. Itu yang terjadi pada
       * 26 Agustus 2026: layar berbunyi "belum ada anggota aktif" padahal sepuluh anggota aktif
       * ada, dan yang salah adalah pembanding di sisi kita.
       *
       * Yang gagal dikatakan gagal. Pembuatan tugas tetap jalan tanpa daftar itu.
       */
      fetchMembers(session.auth).then(
        (m) => ({ ok: true as const, m: m as readonly Member[] }),
        () => ({ ok: false as const, m: [] as readonly Member[] }),
      ),
      /* Peran menentukan siapa KURIR; gagal memuatnya berarti tidak ada yang ditawarkan
         (gagal-tertutup), bukan semua orang ditawarkan. */
      fetchRoleAssignments(session.auth).catch(() => []),
      /*
       * Surat jalan ERP — OPSIONAL, dan kegagalannya sengaja ditelan menjadi daftar kosong.
       *
       * Beda dari daftar anggota di atas, dan bedanya disengaja: jalur tarik boleh saja belum
       * dikonfigurasi (503) atau ERP-nya sedang tak terjangkau, dan layar Buat Tugas harus tetap
       * dapat dipakai dengan mengetik manual seperti sebelum fitur ini ada. Fitur yang mati tidak
       * boleh mematikan fitur yang sudah berjalan.
       */
      fetchErpShipments(session.auth).catch((): readonly SuratJalanErp[] => []),
    ]);
    return { types, members, roles, suratJalan };
  }, [session.auth]);

  const [taskTypeCode, setTaskTypeCode] = useState('');
  const [title, setTitle] = useState('');
  const [priority, setPriority] = useState('medium');
  const [destinationName, setDestinationName] = useState('');
  const [destinationAddress, setDestinationAddress] = useState('');
  const [instructions, setInstructions] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const [delivery, setDelivery] = useState<DeliveryBlockDraft>({
    deliveryOrderNumber: '',
    customerName: '',
    contactPerson: '',
    contactPhone: '',
    pickupLocationName: '',
    pickupAddress: '',
    deliveryNotes: '',
    items: [emptyItem(1)],
  });
  const [documents, setDocuments] = useState(presetLengkap);
  const [dokumenRinci, setDokumenRinci] = useState(false);
  /*
   * Petugas dipilih DI DALAM formulir — arahan Owner 26 Agustus 2026: "gak usah keluar dulu".
   *
   * Sebelumnya penugasan adalah langkah TERPISAH sesudah menyimpan, dan langkah terpisah itu
   * punya dua cacat sekaligus: ia memecah satu pekerjaan menjadi dua layar, dan bila daftarnya
   * kosong pemakai ditinggalkan di layar yang tidak menjelaskan apa-apa. Di dalam formulir,
   * seluruh keputusan terlihat sekaligus sebelum apa pun tersimpan.
   *
   * Kosong = belum ditentukan, dan itu SAH: tugas tanpa penerima terlihat di daftar (#833),
   * jadi menundanya bukan jalan buntu. Dibiarkan opsional dengan sengaja — isian wajib yang
   * ke-sekian adalah cara tombol simpan tampak mati lagi.
   */
  const [petugas, setPetugas] = useState('');
  const [gagalTugaskan, setGagalTugaskan] = useState(false);
  const [isianLain, setIsianLain] = useState(false);
  /*
   * `shipmentId` DO yang dipilih dari ERP, atau string kosong bila manajer mengetik manual.
   *
   * Menyimpan ID-nya, bukan seluruh objeknya: yang dibutuhkan saat mengirim hanyalah kunci
   * idempotensi dan rujukan eksternal, dan objek yang disimpan ganda akan menyimpang dari daftar
   * begitu daftarnya dimuat ulang.
   */
  const [doTerpilih, setDoTerpilih] = useState('');

  /*
   * Tugas berhasil dibuat tetapi daftar berkasnya gagal tersimpan — dua panggilan, dan yang
   * kedua bisa gagal sendiri. Tidak boleh ditelan: tugasnya sudah nyata dan sudah punya nomor,
   * jadi yang benar adalah menunjukkan tugas itu dan mengarahkan ke tempat daftarnya bisa
   * disusun ulang, bukan memutar pengguna kembali ke formulir kosong.
   */
  const [checklistFailed, setChecklistFailed] = useState<string | null>(null);

  const judulEfektif = judulTugas(taskTypeCode, title, destinationName);
  const titleProblem =
    judulEfektif.length === 0
      ? isTukarFaktur(taskTypeCode)
        ? null // ditutupi `pelangganProblem` — satu kekurangan, satu pesan
        : 'Tuliskan judul tugas.'
      : null;
  const typeProblem = taskTypeCode.length === 0 ? 'Pilih jenis tugas.' : null;
  const [submitted, setSubmitted] = useState(false);

  /*
   * Jenis tugas terpilih diambil dari daftar server, BUKAN dari kode ketika belum siap —
   * `requiresProof` (WKF-003, #1156) satu-satunya sumber yang benar untuk kategori kustom.
   */
  const daftarJenis = state.status === 'ready' ? state.data.types : [];
  const tipeTerpilih = daftarJenis.find((t) => t.code === taskTypeCode) ?? null;
  const isDelivery = requiresDeliveryBlock(tipeTerpilih?.requiresProof ?? false);
  const tukarFaktur = isTukarFaktur(taskTypeCode);
  /* Kedua jenis membawa berkas; hanya pengiriman yang membawa barang. */
  const pakaiDokumen = isDelivery || tukarFaktur;
  const deliveryProblems = isDelivery ? validateDeliveryBlock(delivery) : [];

  /*
   * Tukar faktur TANPA satu berkas pun bukan tukar faktur — ia tugas kosong yang tak dapat
   * ditagih apa pun setelahnya. Ditegakkan di sini karena daftar dokumen dikirim lewat
   * panggilan KEDUA, sehingga server tidak dapat menuntutnya secara atomik saat tugas dibuat.
   */
  const dokumenProblem =
    tukarFaktur && checklistPayload(documents).length === 0
      ? 'Pilih minimal satu berkas. Tukar faktur tanpa berkas tidak dapat ditagih.'
      : null;
  const pelangganProblem =
    tukarFaktur && destinationName.trim().length === 0
      ? 'Isi nama pelanggan yang menerima berkas.'
      : null;
  const problemFor = (field: string): string | null =>
    deliveryProblems.find((p) => p.field === field)?.message ?? null;

  /*
   * Seluruh kekurangan dalam SATU daftar — dan daftar yang sama dipakai untuk MENOLAK maupun
   * untuk MENJELASKAN.
   *
   * Sebelumnya keduanya terpisah: `submit` menolak lewat serangkaian `return`, sementara
   * pesannya dirender di sebelah masing-masing isian. Pada formulir sepanjang ini, isian yang
   * kurang bisa berada dua layar di atas tombol — jadi menekan "Buat tugas" tidak menghasilkan
   * apa pun yang terlihat, dan tombolnya tampak MATI. Owner melaporkannya persis begitu:
   * "buat tugas tidak bisa di klik" (26 Agustus 2026).
   *
   * Satu daftar berarti penolakan dan penjelasan tidak mungkin lagi berbeda isi.
   */
  const semuaMasalah: readonly string[] = [
    typeProblem,
    titleProblem,
    pelangganProblem,
    dokumenProblem,
    ...deliveryProblems.map((p) => p.message),
  ].filter((m): m is string => m !== null);

  const suratTerpilih =
    state.status === 'ready'
      ? (state.data.suratJalan.find((sj) => sj.shipmentId === doTerpilih) ?? null)
      : null;
  /** Field pengiriman dikunci saat isinya datang dari ERP. */
  const terkunci = suratTerpilih !== null;

  /**
   * Memilih surat jalan: isi field dari ERP, atau kosongkan kembali bila kembali ke ketik manual.
   *
   * Judul ikut diisi dengan bentuk yang SAMA dengan jalur dorong (`Pengiriman <nomor DO>`) supaya
   * satu DO menghasilkan judul yang sama dari jalur mana pun ia lahir — kalau berbeda, dua orang
   * yang membicarakan tugas yang sama akan menyebut dua nama.
   */
  function pilihSuratJalan(shipmentId: string): void {
    setDoTerpilih(shipmentId);
    if (state.status !== 'ready') return;
    const sj = state.data.suratJalan.find((x) => x.shipmentId === shipmentId);
    if (!sj) {
      /*
       * Kembali ke ketik manual TIDAK mengosongkan apa pun. Manajer yang tidak sengaja memilih
       * DO lalu membatalkannya akan kehilangan seluruh ketikannya kalau dikosongkan — dan yang
       * hilang karena kesalahan sekali klik tidak akan diketik ulang dengan senang hati.
       */
      return;
    }
    setTitle(judulDariSuratJalan(sj));
    setDestinationName(sj.customerName?.trim() ?? '');
    setDestinationAddress(sj.destinationAddress?.trim() ?? '');
    setDelivery({
      ...delivery,
      deliveryOrderNumber: sj.deliveryOrderNumber?.trim() ?? '',
      customerName: sj.customerName?.trim() ?? '',
      contactPhone: sj.contactPhone?.trim() ?? '',
      deliveryNotes: sj.deliveryNotes?.trim() ?? '',
      items: sj.items.map((i) => ({
        lineNo: i.lineNo,
        itemName: i.itemName,
        /*
         * Diringkas UNTUK DIBACA — "30.0000" menjadi "30". Yang dikirim ke server tidak ikut
         * berubah: badan permintaannya dibangun dari `blokPengirimanDari(suratTerpilih)`, yaitu
         * surat jalan ERP asli, bukan dari isian ini.
         */
        quantity: jumlahRingkas(i.quantity),
        sku: i.sku ?? '',
        unit: i.unit ?? '',
      })),
    });
  }

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setSubmitted(true);
    setError(null);
    if (semuaMasalah.length > 0) return;

    setSending(true);
    try {
      /*
       * Kunci idempotensi jalur tarik BUKAN kunci acak biasa, melainkan `erp:shipment:<id>` —
       * bentuk yang sama persis dengan jalur dorong. Itu yang membuat tombol "Kirim ke Workforce"
       * di ERP menabrak tugas ini alih-alih melahirkan tugas kedua untuk satu surat jalan.
       * Dijaga `tarik-do.spec.ts`, yang membandingkan bentuknya ke fungsi asli di sisi API.
       */
      const kunci = suratTerpilih ? kunciIdempotensiErp(suratTerpilih.shipmentId) : keys.current();
      // Badan disusun dan dikirim lewat `kirimTugasBaru` (#1655) supaya dapat diuji.
      const created = await kirimTugasBaru(createTask, session.auth, kunci, {
        suratTerpilih,
        assetId: assetIdParam,
        taskTypeCode,
        title: judulEfektif,
        priority,
        destinationName,
        destinationAddress,
        instructions,
        isDelivery,
        delivery,
      });
      keys.rotate();

      const berkas = checklistPayload(documents);
      if (pakaiDokumen && berkas.length > 0) {
        try {
          await setTaskDocuments(session.auth, created.task.id, keys.current(), berkas);
          keys.rotate();
        } catch {
          // Tugasnya sudah ada. Yang gagal hanya daftarnya — katakan apa adanya.
          setChecklistFailed(created.task.id);
          return;
        }
      }

      /*
       * Ditugaskan SEBELUM berpindah, dan kegagalannya tidak membatalkan tugas yang sudah jadi:
       * tugas tanpa penerima masih dapat ditugaskan dari layar detail, sedangkan menampilkan
       * kegagalan sebagai "tugas gagal dibuat" akan membuat orang menekan simpan dua kali.
       */
      if (petugas.length > 0) {
        try {
          await assignTask(session.auth, created.task.id, keys.current(), petugas);
          keys.rotate();
        } catch {
          setGagalTugaskan(true);
        }
      }
      navigate(`/tasks/${created.task.id}`);
    } catch (caught: unknown) {
      setError(
        caught instanceof ApiError
          ? caught
          : new ApiError(0, 'UNKNOWN', 'Kesalahan tidak dikenal.', null),
      );
    } finally {
      setSending(false);
    }
  }

  if (state.status === 'loading') return <LoadingState label="Memuat jenis tugas…" />;
  if (state.status === 'error') return <ErrorCard error={state.error} />;

  // `sales_visit` TIDAK ditawarkan sama sekali — Sales Visit tetap milik CRM (TASK-010).
  const types = state.data.types.filter(
    (t) => t.isActive && t.code.trim().toUpperCase() !== 'SALES_VISIT',
  );
  const orang = kurirTersedia(state.data.members.m, state.data.roles);
  const daftarOrangGagal = !state.data.members.ok;

  /*
   * Setelah tersimpan, layar ini berhenti menjadi formulir dan menjadi satu pertanyaan.
   * Melewatkannya tetap boleh — tugas tanpa penerima kini terlihat di daftar (#833), jadi
   * "nanti saja" bukan lagi jalan buntu.
   */
  return (
    <>
      <form className="card" onSubmit={submit} noValidate>
        <h2>Buat tugas</h2>
        {assetIdParam && !suratTerpilih ? (
          <p className="muted small">Tiket ini akan ditautkan ke alat terpilih.</p>
        ) : null}

        <label htmlFor="type">Jenis tugas</label>
        <select id="type" value={taskTypeCode} onChange={(e) => setTaskTypeCode(e.target.value)}>
          <option value="">Pilih jenis…</option>
          {types.map((t) => (
            <option key={t.id} value={t.code}>
              {t.name}
            </option>
          ))}
        </select>
        {submitted && typeProblem ? (
          <p className="warning small" role="alert">
            {typeProblem}
          </p>
        ) : null}

        <label htmlFor="title">Judul</label>
        <input id="title" value={title} onChange={(e) => setTitle(e.target.value)} />
        {submitted && titleProblem ? (
          <p className="warning small" role="alert">
            {titleProblem}
          </p>
        ) : null}

        {tukarFaktur ? (
          <>
            {/*
             * Untuk tukar faktur, tujuan BUKAN isian opsional — berkasnya diserahkan kepada
             * seseorang di suatu tempat, dan tanpa itu kurir tidak tahu harus ke mana. Karena
             * jenis ini tidak punya blok pengiriman, nama pelanggan ditumpangkan pada tujuan
             * tugas biasa alih-alih menambah tabel yang isinya satu kolom.
             */}
            <label htmlFor="pelanggan">Pelanggan penerima berkas</label>
            <input
              id="pelanggan"
              value={destinationName}
              onChange={(e) => setDestinationName(e.target.value)}
              readOnly={terkunci}
            />
            {submitted && pelangganProblem ? (
              <p className="warning small" role="alert">
                {pelangganProblem}
              </p>
            ) : null}
          </>
        ) : null}

        {/*
         * Isian opsional dilipat.
         *
         * Enam isian yang hampir selalu dilewati membuat yang WAJIB tenggelam di antaranya, dan
         * di layar telepon seluruhnya menuntut menggulir sebelum tombol simpan terlihat. Dilipat,
         * bukan dibuang: yang jarang dipakai tetap ada bagi yang membutuhkannya.
         */}
        <details
          className="isian-lain"
          open={isianLain}
          onToggle={(e) => setIsianLain(e.currentTarget.open)}
        >
          <summary>Isian lain (prioritas, tujuan, instruksi)</summary>

          <label htmlFor="priority">Prioritas</label>
          <select id="priority" value={priority} onChange={(e) => setPriority(e.target.value)}>
            {PRIORITIES.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>

          <label htmlFor="destName">Nama tujuan</label>
          <input
            id="destName"
            value={destinationName}
            onChange={(e) => setDestinationName(e.target.value)}
            readOnly={terkunci}
          />

          <label htmlFor="destAddr">Alamat tujuan</label>
          <input
            id="destAddr"
            value={destinationAddress}
            onChange={(e) => setDestinationAddress(e.target.value)}
            readOnly={terkunci}
          />

          <label htmlFor="instructions">Instruksi</label>
          <textarea
            id="instructions"
            rows={3}
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
          />
        </details>

        {isDelivery ? (
          <>
            <fieldset className="delivery-block">
              <legend>Pengiriman</legend>

              {/*
                Pemilih surat jalan ERP. Muncul HANYA bila daftarnya berhasil dimuat — bila jalur
                tarik belum dikonfigurasi atau ERP tak terjangkau, seluruh blok ini tidak ada dan
                layar bekerja persis seperti sebelum fitur ini: ketik manual.

                Memilih DO mengisi field di bawahnya dan MENGUNCINYA. Field yang terisi dari ERP
                lalu masih dapat diketik ulang akan menghasilkan tugas yang mengaku berasal dari
                DO tertentu sambil memuat alamat yang berbeda — dan yang membaca perbedaannya
                nanti adalah kurir di depan pintu yang salah.
              */}
              {state.data.suratJalan.length > 0 ? (
                <>
                  <label htmlFor="suratJalan">Ambil dari surat jalan ERP</label>
                  <select
                    id="suratJalan"
                    value={doTerpilih}
                    onChange={(e) => pilihSuratJalan(e.target.value)}
                  >
                    <option value="">{OPSI_TANPA_DO}</option>
                    {state.data.suratJalan.map((sj) => (
                      <option key={sj.shipmentId} value={sj.shipmentId}>
                        {labelSuratJalan(sj)}
                      </option>
                    ))}
                  </select>
                  <p className="muted small">
                    {doTerpilih
                      ? `Isian di bawah datang dari ERP dan tidak dapat diubah. Kembalikan pilihan di atas ke "${OPSI_TANPA_DO}" untuk mengisinya sendiri.`
                      : 'Memilih surat jalan mengisi pelanggan, alamat, dan daftar barang otomatis.'}
                  </p>
                  {suratTerpilih?.sudahAdaTugas ? (
                    /*
                      DO yang sudah punya tugas tetap boleh dipilih, dan peringatannya menyebut
                      NOMOR tugasnya supaya manajer dapat memeriksanya sendiri. Idempotensi
                      mencegah tugas kedua — tetapi mengandalkan pengaman untuk hal yang dapat
                      dikatakan lebih dulu adalah cara pengaman berubah menjadi jalur normal.
                    */
                    <p className="warning">
                      Surat jalan ini sudah punya tugas {suratTerpilih.taskNumber ?? ''}. Menyimpan
                      lagi tidak akan membuat tugas kedua, tetapi periksa dulu sebelum melanjutkan.
                    </p>
                  ) : null}
                </>
              ) : null}

              <label htmlFor="customerName">Nama pelanggan penerima</label>
              <input
                id="customerName"
                value={delivery.customerName}
                onChange={(e) => setDelivery({ ...delivery, customerName: e.target.value })}
                readOnly={terkunci}
              />
              {submitted && problemFor('customerName') ? (
                <p className="warning small" role="alert">
                  {problemFor('customerName')}
                </p>
              ) : null}

              <h3 className="small">Barang</h3>
              {submitted && problemFor('items') ? (
                <p className="warning small" role="alert">
                  {problemFor('items')}
                </p>
              ) : null}

              {delivery.items.map((item) => (
                <div key={item.lineNo} className="delivery-item">
                  <span className="line-no">{item.lineNo}.</span>
                  <input
                    aria-label={`Nama barang baris ${item.lineNo}`}
                    placeholder="Nama barang"
                    value={item.itemName}
                    onChange={(e) =>
                      setDelivery({
                        ...delivery,
                        items: delivery.items.map((it) =>
                          it.lineNo === item.lineNo ? { ...it, itemName: e.target.value } : it,
                        ),
                      })
                    }
                  />
                  <input
                    aria-label={`Jumlah baris ${item.lineNo}`}
                    className="qty"
                    inputMode="decimal"
                    placeholder="Jml"
                    value={item.quantity}
                    onChange={(e) =>
                      setDelivery({
                        ...delivery,
                        items: delivery.items.map((it) =>
                          it.lineNo === item.lineNo ? { ...it, quantity: e.target.value } : it,
                        ),
                      })
                    }
                  />
                  <input
                    aria-label={`Satuan baris ${item.lineNo}`}
                    className="unit"
                    placeholder="Satuan"
                    value={item.unit}
                    onChange={(e) =>
                      setDelivery({
                        ...delivery,
                        items: delivery.items.map((it) =>
                          it.lineNo === item.lineNo ? { ...it, unit: e.target.value } : it,
                        ),
                      })
                    }
                  />
                  <button
                    type="button"
                    className="link"
                    onClick={() =>
                      setDelivery({ ...delivery, items: removeItem(delivery.items, item.lineNo) })
                    }
                  >
                    Hapus
                  </button>
                </div>
              ))}

              {delivery.items.flatMap((item) => {
                const masalah = [
                  problemFor(`items.${item.lineNo}.itemName`),
                  problemFor(`items.${item.lineNo}.quantity`),
                ].filter((m): m is string => m !== null);
                return submitted
                  ? masalah.map((m) => (
                      <p key={m} className="warning small" role="alert">
                        {m}
                      </p>
                    ))
                  : [];
              })}

              <button
                type="button"
                className="link"
                onClick={() => setDelivery({ ...delivery, items: addItem(delivery.items) })}
              >
                Tambah barang
              </button>

              {/* Sama alasannya dengan "Isian lain" di atas: yang jarang diisi tidak boleh
                  mendorong tombol simpan keluar layar. */}
              <details className="isian-lain">
                <summary>Rincian lain (surat jalan, kontak, asal barang, catatan)</summary>

                <label htmlFor="doNumber">Nomor surat jalan</label>
                <input
                  id="doNumber"
                  value={delivery.deliveryOrderNumber}
                  onChange={(e) =>
                    setDelivery({ ...delivery, deliveryOrderNumber: e.target.value })
                  }
                />

                <label htmlFor="contactPerson">Nama kontak di lokasi</label>
                <input
                  id="contactPerson"
                  value={delivery.contactPerson}
                  onChange={(e) => setDelivery({ ...delivery, contactPerson: e.target.value })}
                  readOnly={terkunci}
                />

                <label htmlFor="contactPhone">Telepon kontak</label>
                <input
                  id="contactPhone"
                  value={delivery.contactPhone}
                  onChange={(e) => setDelivery({ ...delivery, contactPhone: e.target.value })}
                  readOnly={terkunci}
                />

                <label htmlFor="pickupName">Ambil barang dari</label>
                <input
                  id="pickupName"
                  value={delivery.pickupLocationName}
                  onChange={(e) => setDelivery({ ...delivery, pickupLocationName: e.target.value })}
                  readOnly={terkunci}
                />

                <label htmlFor="deliveryNotes">Catatan pengiriman</label>
                <textarea
                  id="deliveryNotes"
                  rows={2}
                  value={delivery.deliveryNotes}
                  onChange={(e) => setDelivery({ ...delivery, deliveryNotes: e.target.value })}
                  readOnly={terkunci}
                />
              </details>
            </fieldset>

            <DocumentChecklistPicker
              rows={documents}
              onChange={setDocuments}
              disabled={sending}
              rinci={dokumenRinci}
              onRinciChange={setDokumenRinci}
            />
          </>
        ) : null}

        {tukarFaktur ? (
          <>
            <DocumentChecklistPicker
              rows={documents}
              onChange={setDocuments}
              disabled={sending}
              rinci={dokumenRinci}
              onRinciChange={setDokumenRinci}
            />
            {submitted && dokumenProblem ? (
              <p className="warning small" role="alert">
                {dokumenProblem}
              </p>
            ) : null}
          </>
        ) : null}

        {/*
         * Petugas — di dalam formulir, bukan langkah sesudahnya. Dibiarkan opsional: tugas tanpa
         * penerima terlihat di daftar (#833), jadi menundanya bukan jalan buntu.
         */}
        <label htmlFor="petugas">Kurir</label>
        {daftarOrangGagal ? (
          <p className="warning small" role="alert">
            Daftar orang gagal dimuat, jadi penugasan belum bisa dipilih di sini. Tugasnya tetap
            dapat dibuat — tugaskan nanti dari layar detail.
          </p>
        ) : (
          <select id="petugas" value={petugas} onChange={(e) => setPetugas(e.target.value)}>
            <option value="">Belum ditentukan</option>
            {orang.map((m) => (
              <option key={m.userId} value={m.userId}>
                {m.displayName}
              </option>
            ))}
          </select>
        )}
        {!daftarOrangGagal && orang.length === 0 ? (
          <p className="muted small">
            Belum ada kurir aktif. Beri peran Kurir lewat layar Karyawan lebih dulu.
          </p>
        ) : null}

        {submitted && semuaMasalah.length > 0 ? (
          <div className="card warn" role="alert">
            <p>Belum bisa disimpan — yang masih kurang:</p>
            <ul className="list-ringkas">
              {semuaMasalah.map((m) => (
                <li key={m}>{m}</li>
              ))}
            </ul>
          </div>
        ) : null}

        <button type="submit" className="button-primary" disabled={sending}>
          {sending ? 'Menyimpan…' : 'Buat tugas'}
        </button>
      </form>

      {checklistFailed ? (
        <div className="card warn" role="alert">
          <p>
            Tugas sudah dibuat, tetapi daftar dokumen penagihan gagal disimpan. Bukalah tugasnya dan
            susun daftarnya di sana — jangan membuat tugas baru, nanti kurir menerima dua.
          </p>
          <Link to={`/tasks/${checklistFailed}`}>Buka tugas</Link>
        </div>
      ) : null}

      {gagalTugaskan ? (
        <div className="card warn" role="alert">
          <p>
            Tugas berhasil dibuat, tetapi penugasan ke petugas gagal. Buka tugasnya dan tugaskan
            dari sana — jangan membuat tugas baru.
          </p>
        </div>
      ) : null}

      {error ? <ErrorCard error={error} /> : null}
    </>
  );
}
